/** @typedef {'horizontal'|'vertical'|'grid1'|'grid2'|'grid3'|'grid4'|'grid5'|'grid6'} GalleryViewType */

export const GALLERY_VIEW_TYPES = [
  'horizontal',
  'vertical',
  'grid1',
  'grid2',
  'grid3',
  'grid4',
  'grid5',
  'grid6',
];

export const GALLERY_LAYOUT_LABELS = {
  horizontal: 'Horizontal',
  vertical: 'Vertical',
  grid1: 'Grid 1',
  grid2: 'Grid 2',
  grid3: 'Grid 3',
  grid4: 'Grid 4',
  grid5: 'Grid 5',
  grid6: 'Grid 6',
};

/** @param {string | undefined} layout */
export function normalizeGalleryLayout(layout) {
  return GALLERY_VIEW_TYPES.includes(layout) ? layout : 'grid5';
}

/**
 * Grid 1: two cells on top row, one full-width below — repeats every 3 images.
 * @param {number} index
 * @param {number} total
 */
function grid1Placement(index, total) {
  if (total === 2) {
    if (index === 0) return { gridColumn: '1 / 2', gridRow: '1 / 2' };
    return { gridColumn: '2 / 3', gridRow: '1 / 2' };
  }
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;

  if (index < completeGroups * 3) {
    const g = Math.floor(index / 3);
    const l = index % 3;
    const top = 2 * g + 1;
    const mid = 2 * g + 2;
    const bot = 2 * g + 3;
    if (l === 0) return { gridColumn: '1 / 2', gridRow: `${top} / ${mid}` };
    if (l === 1) return { gridColumn: '2 / 3', gridRow: `${top} / ${mid}` };
    return { gridColumn: '1 / 3', gridRow: `${mid} / ${bot}` };
  }

  const startLine = 2 * completeGroups + 1;
  const offset = index - completeGroups * 3;
  if (remainder === 1) {
    return { gridColumn: '1 / 3', gridRow: `${startLine} / ${startLine + 1}` };
  }
  if (remainder === 2) {
    if (offset === 0) return { gridColumn: '1 / 2', gridRow: `${startLine} / ${startLine + 1}` };
    return { gridColumn: '2 / 3', gridRow: `${startLine} / ${startLine + 1}` };
  }
  return { gridColumn: '1 / 2', gridRow: '1 / 2' };
}

/**
 * Grid 2: one full-width row on top, two cells below — repeats every 3 images.
 */
function grid2Placement(index, total) {
  if (total === 2) {
    if (index === 0) return { gridColumn: '1 / 3', gridRow: '1 / 2' };
    return { gridColumn: '1 / 3', gridRow: '2 / 3' };
  }
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;

  if (index < completeGroups * 3) {
    const g = Math.floor(index / 3);
    const l = index % 3;
    const r1 = 2 * g + 1;
    const r2 = 2 * g + 2;
    const r3 = 2 * g + 3;
    if (l === 0) return { gridColumn: '1 / 3', gridRow: `${r1} / ${r2}` };
    if (l === 1) return { gridColumn: '1 / 2', gridRow: `${r2} / ${r3}` };
    return { gridColumn: '2 / 3', gridRow: `${r2} / ${r3}` };
  }

  const startLine = 2 * completeGroups + 1;
  const offset = index - completeGroups * 3;
  if (remainder === 1) {
    return { gridColumn: '1 / 3', gridRow: `${startLine} / ${startLine + 1}` };
  }
  if (remainder === 2) {
    if (offset === 0) return { gridColumn: '1 / 3', gridRow: `${startLine} / ${startLine + 1}` };
    return { gridColumn: '1 / 3', gridRow: `${startLine + 1} / ${startLine + 2}` };
  }
  return { gridColumn: '1 / 2', gridRow: '1 / 2' };
}

/**
 * Grid 3: tall left + two stacked right — repeats every 3 images.
 */
function grid3Placement(index, total) {
  if (total === 2) {
    if (index === 0) return { gridColumn: '1 / 2', gridRow: '1 / 3' };
    return { gridColumn: '2 / 3', gridRow: '1 / 3' };
  }
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;

  if (index < completeGroups * 3) {
    const g = Math.floor(index / 3);
    const l = index % 3;
    const top = 2 * g + 1;
    const mid = 2 * g + 2;
    const bot = 2 * g + 3;
    if (l === 0) return { gridColumn: '1 / 2', gridRow: `${top} / ${bot}` };
    if (l === 1) return { gridColumn: '2 / 3', gridRow: `${top} / ${mid}` };
    return { gridColumn: '2 / 3', gridRow: `${mid} / ${bot}` };
  }

  const startLine = 2 * completeGroups + 1;
  const offset = index - completeGroups * 3;
  if (remainder === 1) {
    return { gridColumn: '1 / 3', gridRow: `${startLine} / ${startLine + 1}` };
  }
  if (remainder === 2) {
    if (offset === 0) return { gridColumn: '1 / 2', gridRow: `${startLine} / ${startLine + 2}` };
    return { gridColumn: '2 / 3', gridRow: `${startLine} / ${startLine + 2}` };
  }
  return { gridColumn: '1 / 2', gridRow: '1 / 2' };
}

/**
 * Grid 4: two stacked left + tall right — repeats every 3 images.
 */
function grid4Placement(index, total) {
  if (total === 2) {
    if (index === 0) return { gridColumn: '1 / 2', gridRow: '1 / 3' };
    return { gridColumn: '2 / 3', gridRow: '1 / 3' };
  }
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;

  if (index < completeGroups * 3) {
    const g = Math.floor(index / 3);
    const l = index % 3;
    const top = 2 * g + 1;
    const mid = 2 * g + 2;
    const bot = 2 * g + 3;
    if (l === 0) return { gridColumn: '1 / 2', gridRow: `${top} / ${mid}` };
    if (l === 1) return { gridColumn: '1 / 2', gridRow: `${mid} / ${bot}` };
    return { gridColumn: '2 / 3', gridRow: `${top} / ${bot}` };
  }

  const startLine = 2 * completeGroups + 1;
  const offset = index - completeGroups * 3;
  if (remainder === 1) {
    return { gridColumn: '1 / 3', gridRow: `${startLine} / ${startLine + 1}` };
  }
  if (remainder === 2) {
    if (offset === 0) return { gridColumn: '1 / 2', gridRow: `${startLine} / ${startLine + 1}` };
    return { gridColumn: '1 / 2', gridRow: `${startLine + 1} / ${startLine + 2}` };
  }
  return { gridColumn: '1 / 2', gridRow: '1 / 2' };
}

/** @param {number} index @param {number} total */
function grid1Visual(index, total) {
  if (total === 2) return '1 / 1';
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;
  if (index < completeGroups * 3) {
    const l = index % 3;
    return l === 2 ? '2 / 1' : '1 / 1';
  }
  if (remainder === 1) return '2 / 1';
  return '1 / 1';
}

/** @param {number} index @param {number} total */
function grid2Visual(index, total) {
  if (total === 2) return '2 / 1';
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;
  if (index < completeGroups * 3) {
    const l = index % 3;
    return l === 0 ? '2 / 1' : '1 / 1';
  }
  if (remainder === 1) return '2 / 1';
  return '2 / 1';
}

/** @param {number} index @param {number} total */
function grid3Visual(index, total) {
  if (total === 2) return '1 / 2';
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;
  if (index < completeGroups * 3) {
    const l = index % 3;
    return l === 0 ? '1 / 2' : '1 / 1';
  }
  if (remainder === 1) return '2 / 1';
  return '1 / 2';
}

/** @param {number} index @param {number} total */
function grid4Visual(index, total) {
  if (total === 2) return '1 / 2';
  const completeGroups = Math.floor(total / 3);
  const remainder = total % 3;
  if (index < completeGroups * 3) {
    const l = index % 3;
    return l === 2 ? '1 / 2' : '1 / 1';
  }
  if (remainder === 1) return '2 / 1';
  return '1 / 1';
}

/**
 * CSS aspect-ratio for each cell so shapes match the picker icons (wide / square / tall).
 * @param {GalleryViewType | string | undefined} viewType
 * @param {number} index
 * @param {number} total
 * @returns {string | null} e.g. '1 / 1', '2 / 1', '1 / 2'
 */
export function getGalleryCellAspectRatio(viewType, index, total) {
  if (total <= 0) return null;
  const vt = normalizeGalleryLayout(viewType);
  if (vt === 'horizontal' || vt === 'vertical') return null;
  if (total === 1) return '4 / 3';
  if (vt === 'grid5') return '1 / 1';
  if (vt === 'grid6') return '1 / 1';
  if (vt === 'grid1') return grid1Visual(index, total);
  if (vt === 'grid2') return grid2Visual(index, total);
  if (vt === 'grid3') return grid3Visual(index, total);
  if (vt === 'grid4') return grid4Visual(index, total);
  return '1 / 1';
}

/**
 * CSS grid placement (1-based line indices) for mosaic cells.
 * Container: 2 columns for grid1–grid5, 3 columns for grid6.
 * @param {GalleryViewType | string | undefined} viewType
 * @param {number} index
 * @param {number} total
 * @returns {{ gridColumn: string, gridRow: string } | Record<string, never>}
 */
export function getGalleryItemGridStyle(viewType, index, total) {
  if (total <= 0) return {};
  const vt = normalizeGalleryLayout(viewType);

  if (vt === 'grid5') {
    const row = Math.floor(index / 2) + 1;
    const col = (index % 2) + 1;
    return { gridColumn: `${col} / ${col + 1}`, gridRow: `${row} / ${row + 1}` };
  }

  if (vt === 'grid6') {
    const row = Math.floor(index / 3) + 1;
    const col = (index % 3) + 1;
    return { gridColumn: `${col} / ${col + 1}`, gridRow: `${row} / ${row + 1}` };
  }

  if (total === 1) {
    return { gridColumn: '1 / 3', gridRow: '1 / 3' };
  }

  if (vt === 'grid1') {
    return grid1Placement(index, total);
  }

  if (vt === 'grid2') {
    return grid2Placement(index, total);
  }

  if (vt === 'grid3') {
    return grid3Placement(index, total);
  }

  if (vt === 'grid4') {
    return grid4Placement(index, total);
  }

  return getGalleryItemGridStyle('grid5', index, total);
}

/**
 * Effective layout for display: simple rules when there are 1–2 images.
 * @param {GalleryViewType | string | undefined} layout
 * @param {number} count
 */
export function getEffectiveGalleryLayout(layout, count) {
  if (count <= 0) return 'grid5';
  if (count === 1) return 'vertical';
  if (count === 2) return 'grid5';
  return normalizeGalleryLayout(layout);
}
